import { useState } from 'react';
import {
  Card,
  Button,
  Badge,
  LoadingSkeleton,
  useToast,
} from '@nexora/design-system';
import { useRoles, useCreateRole, useUpdateRole, useDeleteRole, type Role } from './useIamUsers';
import { formatDate } from '../../shared/utils/format';

/* ── Helper ── */

/* ── Component ── */

export function RolesPage(): JSX.Element {
  const { addToast } = useToast();
  const [showForm, setShowForm] = useState(false);
  const [editingRole, setEditingRole] = useState<Role | null>(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const [expandedRoleId, setExpandedRoleId] = useState<string | null>(null);

  const { data, isLoading, error } = useRoles();
  const createMutation = useCreateRole();
  const updateMutation = useUpdateRole();
  const deleteMutation = useDeleteRole();

  // ── Form state ──
  const [formName, setFormName] = useState('');
  const [formPermissionsText, setFormPermissionsText] = useState('');

  const resetForm = (): void => {
    setFormName('');
    setFormPermissionsText('');
    setEditingRole(null);
    setShowForm(false);
  };

  const openEdit = (role: Role): void => {
    setEditingRole(role);
    setFormName(role.name);
    setFormPermissionsText(role.permissions.join(', '));
    setShowForm(true);
  };

  const handleSubmit = (): void => {
    if (!formName.trim()) {
      addToast('Nome do papel é obrigatório.', 'error');
      return;
    }

    const permissions = formPermissionsText
      .split(',')
      .map((p) => p.trim())
      .filter(Boolean);

    if (editingRole) {
      updateMutation.mutate(
        { id: editingRole.id, name: formName, permissions },
        {
          onSuccess: () => {
            addToast('Papel atualizado com sucesso', 'success');
            resetForm();
          },
          onError: (err) => {
            addToast(err instanceof Error ? err.message : 'Erro ao atualizar', 'error');
          },
        },
      );
    } else {
      createMutation.mutate(
        { name: formName, permissions },
        {
          onSuccess: () => {
            addToast('Papel criado com sucesso', 'success');
            resetForm();
          },
          onError: (err) => {
            addToast(err instanceof Error ? err.message : 'Erro ao criar', 'error');
          },
        },
      );
    }
  };

  const handleDelete = (id: string): void => {
    deleteMutation.mutate(id, {
      onSuccess: () => {
        addToast('Papel removido com sucesso', 'success');
        setConfirmDeleteId(null);
      },
      onError: (err) => {
        addToast(err instanceof Error ? err.message : 'Erro ao remover', 'error');
        setConfirmDeleteId(null);
      },
    });
  };

  const isPending = createMutation.isPending || updateMutation.isPending;
  const allPermissions = data?.flatMap((r) => r.permissions) ?? [];
  const uniquePermissions = [...new Set(allPermissions)].sort();

  return (
    <div className="iam-page">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-end gap-md mb-lg">
        <div>
          <h1 className="font-display-lg text-display-lg text-on-surface m-0">Papéis</h1>
          <p className="font-body-md text-body-md text-on-surface-variant mt-xs">Papéis e permissões atribuíveis.</p>
        </div>
        <div className="flex flex-wrap items-center gap-sm">
          <Button variant="primary" size="sm" onClick={() => { resetForm(); setShowForm(true); }}>
            + Novo Papel
          </Button>
        </div>
      </div>

      {/* ── Form Modal ── */}
      {showForm && (
        <div className="modal-overlay" onClick={() => !isPending && resetForm()}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editingRole ? 'Editar Papel' : 'Novo Papel'}</h2>
            </div>
            <div className="modal-body">
              <label className="field-label">Nome do Papel</label>
              <input className="field-input" value={formName} onChange={(e) => setFormName(e.target.value)} placeholder="Ex: Administrador" disabled={isPending} />

              <label className="field-label">Permissões (separadas por vírgula)</label>
              <textarea className="field-textarea" value={formPermissionsText} onChange={(e) => setFormPermissionsText(e.target.value)} placeholder="ex: users:read, users:write, sales:read" rows={4} disabled={isPending} />
              {uniquePermissions.length > 0 && (
                <div className="perms-hint">
                  <span className="hint-label">Permissões existentes:</span>
                  <div className="hint-tags">
                    {uniquePermissions.map((p) => (
                      <button key={p} className="hint-tag" type="button" onClick={() => setFormPermissionsText((prev) => (prev ? `${prev}, ${p}` : p))}>
                        {p}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
            <div className="modal-footer">
              <Button variant="ghost" onClick={resetForm} disabled={isPending}>Cancelar</Button>
              <Button variant="primary" loading={isPending} onClick={handleSubmit}>
                {editingRole ? 'Salvar' : 'Criar'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* ── List ── */}
      <Card>
        {isLoading && (
          <div className="loading-container">
            <LoadingSkeleton count={6} height="32px" />
          </div>
        )}

        {error && (
          <div className="error-container">
            <p className="error-message">Erro ao carregar papéis: {(error as Error).message}</p>
          </div>
        )}

        {!isLoading && !error && (!data || data.length === 0) && (
          <div className="empty-container">
            <p className="empty-message">Nenhum papel encontrado.</p>
            <Button variant="primary" size="sm" onClick={() => setShowForm(true)}>Criar primeiro papel</Button>
          </div>
        )}

        {!isLoading && !error && data && data.length > 0 && (
          <div className="table-wrapper">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Nome</th>
                  <th>Permissões</th>
                  <th>Criado em</th>
                  <th>Ações</th>
                </tr>
              </thead>
              <tbody>
                {data.map((role) => (
                  <tr key={role.id}>
                    <td className="cell-name">{role.name}</td>
                    <td>
                      <div className="perms-cell">
                        <span className="perms-count">{role.permissions.length} permissões</span>
                        <Button size="sm" variant="ghost" onClick={() => setExpandedRoleId(expandedRoleId === role.id ? null : role.id)}>
                          {expandedRoleId === role.id ? '▲' : '▼'}
                        </Button>
                      </div>
                      {expandedRoleId === role.id && (
                        <div className="perms-expanded">
                          {role.permissions.slice(0, 20).map((p) => (
                            <Badge key={p} variant="neutral">{p}</Badge>
                          ))}
                          {role.permissions.length > 20 && <span className="more-perms">+{role.permissions.length - 20} mais</span>}
                        </div>
                      )}
                    </td>
                    <td>{formatDate(role.createdAt)}</td>
                    <td>
                      <div className="action-group">
                        <Button size="sm" variant="outline" onClick={() => openEdit(role)}>Editar</Button>
                        {confirmDeleteId === role.id ? (
                          <div className="inline-confirm">
                            <span className="confirm-text">Remover?</span>
                            <Button size="sm" variant="danger" loading={deleteMutation.isPending} onClick={() => handleDelete(role.id)}>Sim</Button>
                            <Button size="sm" variant="ghost" onClick={() => setConfirmDeleteId(null)} disabled={deleteMutation.isPending}>Não</Button>
                          </div>
                        ) : (
                          <Button size="sm" variant="danger" onClick={() => setConfirmDeleteId(role.id)}>Excluir</Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <style>{`
        .iam-page { padding: 1.5rem; }
        .loading-container { padding: 1rem; display: flex; flex-direction: column; gap: 0.75rem; }
        .error-container { padding: 2rem; text-align: center; }
        .error-message { color: #ba1a1a; font-size: 0.875rem; }
        .empty-container { padding: 2rem; text-align: center; display: flex; flex-direction: column; align-items: center; gap: 1rem; }
        .empty-message { color: #74777f; font-size: 0.875rem; }
        .table-wrapper { overflow-x: auto; }
        .data-table { width: 100%; border-collapse: collapse; font-size: 0.875rem; }
        .data-table th { text-align: left; padding: 0.75rem 1rem; background: #f2f4f6; color: #43474e; font-weight: 600; font-size: 0.75rem; text-transform: uppercase; letter-spacing: 0.05em; border-bottom: 2px solid #e0e3e5; white-space: nowrap; position: sticky; top: 0; z-index: 1;}
        .data-table td { padding: 0.75rem 1rem; border-bottom: 1px solid #eceef0; color: #191c1e; }
        .data-table tbody tr:hover td { background: #eceef0; }
        .cell-name { font-weight: 600; }
        .action-group { display: flex; gap: 0.375rem; flex-wrap: wrap; align-items: center; }
        .inline-confirm { display: flex; align-items: center; gap: 0.375rem; }
        .confirm-text { font-size: 0.8125rem; color: #ba1a1a; font-weight: 600; }
        .perms-cell { display: flex; align-items: center; gap: 0.375rem; }
        .perms-count { font-size: 0.8125rem; color: #43474e; }
        .perms-expanded { display: flex; flex-wrap: wrap; gap: 0.25rem; margin-top: 0.5rem; padding: 0.5rem; background: #f2f4f6; border-radius: 4px; }
        .more-perms { font-size: 0.75rem; color: #74777f; align-self: center; }
        .perms-hint { margin-top: 0.5rem; }
        .hint-label { font-size: 12px; color: #43474e; display: block; margin-bottom: 0.375rem; font-family: 'JetBrains Mono', ui-monospace, monospace; font-weight: 500; letter-spacing: 0.02em;}
        .hint-tags { display: flex; flex-wrap: wrap; gap: 0.25rem; }
        .hint-tag { font-size: 0.6875rem; padding: 0.125rem 0.375rem; background: #d6e3ff; color: #002045; border: none; border-radius: 4px; cursor: pointer; }
        .hint-tag:hover { background: #d6e3ff; }
        .modal-overlay { position: fixed; inset: 0; background: rgba(0,0,0,0.4); display: flex; align-items: center; justify-content: center; z-index: 1000; }
        .modal-content { background: #ffffff; border-radius: 8px; width: 520px; max-width: 90vw; box-shadow: 0 8px 32px rgba(0,0,0,0.15); max-height: 80vh; overflow-y: auto; }
        .modal-header { padding: 1.25rem 1.5rem; border-bottom: 1px solid #e0e3e5; }
        .modal-header h2 { margin: 0; font-size: 1.125rem; color: #191c1e; }
        .modal-body { padding: 1.5rem; display: flex; flex-direction: column; gap: 1rem; }
        .modal-footer { padding: 1rem 1.5rem; border-top: 1px solid #e0e3e5; display: flex; justify-content: flex-end; gap: 0.5rem; }
        .field-label { font-size: 12px; font-weight: 500; color: #43474e; margin-bottom: 0.25rem; font-family: 'JetBrains Mono', ui-monospace, monospace; letter-spacing: 0.02em;}
        .field-input { width: 100%; padding: 0.5rem 0.75rem; border: 1px solid #e0e3e5; border-radius: 4px; font-size: 0.875rem; box-sizing: border-box; }
        .field-input:focus { outline: none; border-color: #002045; box-shadow: 0 0 0 2px rgba(0, 32, 69, 0.15); }
        .field-textarea { width: 100%; padding: 0.5rem 0.75rem; border: 1px solid #e0e3e5; border-radius: 4px; font-size: 0.875rem; box-sizing: border-box; font-family: 'SFMono-Regular', Consolas, monospace; resize: vertical; }
        .field-textarea:focus { outline: none; border-color: #002045; box-shadow: 0 0 0 2px rgba(0, 32, 69, 0.15); }
      `}</style>
    </div>
  );
}
